import {DefaultValue, selector, selectorFamily, waitForAll} from 'recoil';

import {
  companiesFilterQueryTrigger,
  companiesForMapQueryTrigger,
  companiesForMapState,
  companiesQueryTrigger,
  companiesSelectedState,
  companiesSortQueryTrigger,
  companiesState,
  companyPageable,
  currentCompanyIdState,
  currentCompanyState,
  mapWithCompanyNamesState,
} from './atoms';

import {CompanyService} from '../../api/services/company.service';

import {ICompany} from '../../shared/types/company.types';

export const companiesQuery = selector({
  key: 'companiesQuery',
  get: async ({get}) => {
    get(companiesQueryTrigger);
    const [pageable, sort, filter] = get(
      waitForAll([companyPageable, companiesSortQueryTrigger, companiesFilterQueryTrigger]),
    );

    const res = await CompanyService.getAll({
      spec: {
        ...filter,
      },
      pageable: {
        order: [sort?.[0]?.field || 'fullCompanyName', sort?.[0]?.sort === 'asc' ? 'ASC' : 'DESC'],
        ...pageable,
      },
    });

    return res.data;
  },
  set: ({set}, newValue) => {
    if (newValue) {
      set(companiesState, newValue);
    }
  },
});

export const companiesForMapQuery = selector({
  key: 'companiesForMapQuery',
  get: async ({get}) => {
    get(companiesForMapQueryTrigger);
    const res = await CompanyService.getAll({
      pageable: {
        size: 100,
      },
    });

    return res.data.content;
  },
  set: ({set, get}, newValue) => {
    if (newValue) {
      set(companiesForMapState, newValue);
    }

    if (newValue instanceof DefaultValue) return;
    const mapWithNames = get(mapWithCompanyNamesState);

    if (!newValue || newValue.length < 1) return;

    newValue.forEach((i) => {
      mapWithNames.set(i.id, i.fullCompanyName);
      mapWithNames.set(i.fullCompanyName.toUpperCase(), i.id);
    });
  },
});

export const companyByIdQuery = selectorFamily({
  key: 'companyByIdQuery',
  get:
    (id) =>
    async ({get}) => {
      get(companiesQueryTrigger);

      if (!id) return null;

      const res = await CompanyService.findByIDs([String(id)]);

      return res.data[0];
    },
  set:
    () =>
    ({set}, newValue) => {
      set(currentCompanyState, newValue);
    },
});

export const currentCompanySelector = selector({
  key: 'currentCompanySelector',
  get: ({get}) => {
    const {content: companies} = get(companiesState);
    const index = get(currentCompanyIdState);

    return companies.find((i) => i.id === index) as ICompany;
  },
});

export const companyByIdSelectorFamily = selectorFamily({
  key: 'companyByIdSelectorFamily',
  get:
    (id) =>
    ({get}) => {
      if (!id) return null;

      const {content: companies} = get(companiesState);

      return companies.find((i) => i.id === id) as ICompany;
    },
});

export const companySelectedIdsSelector = selector({
  key: 'companySelectedIdsSelector',
  get: ({get}) => {
    const companies = get(companiesSelectedState);

    return Object.entries(companies).reduce<string[]>((prev, cur) => {
      return [...prev, ...cur[1]];
    }, []);
  },
});
